// The header bell (Manus "notifications", approved 2026-11-02): the 4-hour warnings and breaches
// that the rules engine recorded on the rows now on the board. The recorded alert decides an
// item, never the clock (same rule as the row colour). No push, no sound, no new alert logic.
// Read or unread is kept in this browser only.

import type { BoardEntry, BoardTimer } from '@diagnuvo/domain';
import { alertHandling, displayName, zoneLabel } from './board-state.ts';

export interface AlertNotification {
  /** `${encounterId}:${level}`: a warning and a later breach are 2 items. */
  id: string;
  encounterId: string;
  level: 'warning' | 'breach';
  /** The threshold time of the recorded alert (ISO 8601). */
  at: string;
  title: string;
  name: string;
  place: string;
  /** The row's 4-hour timer, for acknowledgement (L4.2). */
  timer: BoardTimer;
  /** "Acknowledged hh:mm" or "Escalated to … hh:mm", when recorded. */
  handling?: string;
}

/** One item per row whose ED 4-hour timer has a recorded warning or breach, newest first. */
export function notificationsFrom(rows: Iterable<BoardEntry>): AlertNotification[] {
  const items: AlertNotification[] = [];
  for (const row of rows) {
    const timer = row.timers.find((t) => t.id === 'ed-4h');
    if (timer === undefined || timer.alert === 'none') continue;
    const level = timer.alert;
    const handling = alertHandling(timer);
    const place = [zoneLabel(row.location?.room), row.location?.bed].filter(Boolean).join(' · ');
    items.push({
      id: `${row.encounterId}:${level}`,
      encounterId: row.encounterId,
      level,
      at: level === 'breach' ? timer.breachAt : timer.warnAt,
      title: level === 'breach' ? 'Breach recorded' : 'Warning recorded',
      name: displayName(row.patient),
      place,
      timer,
      ...(handling !== undefined && { handling }),
    });
  }
  return items.sort((a, b) => Date.parse(b.at) - Date.parse(a.at) || a.name.localeCompare(b.name));
}

export const unreadCount = (
  items: readonly AlertNotification[],
  seen: ReadonlySet<string>,
): number => items.filter((i) => !seen.has(i.id)).length;

const KEY = 'diagnuvo.seen-alerts';

/** The item IDs seen in this browser. Storage that is blocked or broken gives none. */
export function loadSeen(): Set<string> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return new Set(Array.isArray(value) ? value.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Saves the IDs now shown as seen (only those, so the list stays small). */
export function saveSeen(ids: Iterable<string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...ids]));
  } catch {
    // storage blocked: the count resets on the next visit
  }
}
